import React, { useRef, useState } from "react";
import { BrowserRouter, Outlet, Navigate, Route, Routes, useLocation } from "react-router-dom";

// components
import { withToastProvider } from "./components/toast";
import { TableRefreshProvider } from "context/TableRefresh";

// layouts
import BaseLayout from "./layouts/BaseLayout";
import LoginLayout from "./layouts/LoginLayout";
import LoadingLayout from "./layouts/LoadingLayout";
import DispatchLayout from "layouts/DispatchLayout";

// misc
import Theme from "./theme";
import { UserProvider } from "./context/User";
import { useError } from "./context/Error";
import { refreshToken } from "./api/Auth/index";

import RenderCompleted from "./hooks/RenderCompleted";
import useVersionCheck from "./hooks/useVersionCheck";

import "./App.scss";

// third party modules
import axios from "axios";
import { useEffect } from "react";
import useAuthService from "hooks/useAuthService";

// containers
import ForbiddenPage from "./containers/Errors/403";
import NotFoundPage from "./containers/Errors/404";

import ForgotPassword from "./containers/Auth/ForgotPassword";
import Login from "./containers/Auth/Login";
import ResetPassword from "./containers/Auth/ResetPassword";
import Register from "./containers/Auth/Register";

import Dashboard from "./containers/Dashboard";
import Users from "./containers/Users";
import Supers from "containers/Users/supers";

import Roles from "./containers/Roles";
import RoleCreate from "containers/Roles/create";
import RoleEdit from "containers/Roles/edit";

import Companies from "containers/Companies";
import Company from "containers/Companies/edit";
import ClientCompany from "containers/ClientCompany";
import Clients from "containers/Clients";
import Client from "containers/Clients/view";
import ClientEdit from "containers/Clients/edit";
import ClientCreate from "containers/Clients/create";

import Groups from "containers/Clients/Groups";
import Regions from "containers/Clients/Regions";
import ClientUsers from "containers/Clients/Users";

import Providers from "containers/Providers";
import ProviderCreate from "containers/Providers/create";
import ProviderEdit from "containers/Providers/edit";
import Products from "containers/Products";
import ProductCreate from "containers/Products/create";
import Product from "containers/Products/edit";
import ServiceArea from "containers/ServiceArea";
import Services from "containers/Services";
import Service from "containers/Services/edit";
import ServiceCreate from "containers/Services/create";
import Depot from "containers/Providers/Depots/edit";
import QuoteRequests from "containers/QuoteRequests";
import RfqDuplicates from "containers/QuoteRequests/Duplicates";
import QuoteRequest from "containers/QuoteRequests/view";
import QuoteRequestEdit from "containers/QuoteRequests/edit ";
import Quotes from "containers/Quotes";
import Invoices from "containers/Invoices";
import Projects from "containers/Projects";
import Quote from "containers/Quotes/view";
import Invoice from "containers/Invoices/view";
import Project from "containers/Projects/view";
import QuoteEdit from "containers/Quotes/edit";
import ProjectEdit from "containers/Projects/edit";
import ProjectQuoteCreate from "containers/Projects/quoteCreate";
import ProjectInvoiceCreate from "containers/Projects/invoiceCreate";
import InvoiceEdit from "containers/Invoices/edit";
import PurchaseOrders from "containers/PurchaseOrders";
import PurchaseOrder from "containers/PurchaseOrders/view";
import PurchaseOrderEdit from "containers/PurchaseOrders/edit";
import Provider from "containers/Providers/view";
import PurchaseOrderReplace from "containers/PurchaseOrders/replace";
import ProviderInvoiceReview from "containers/ProviderInvoiceReview";
import Dispatch from "containers/Dispatch";
import AIChat from "containers/AIChat";
import Settings from "containers/Settings";

import Locations from "containers/Locations";
import LocationDetail from "containers/Locations/detail";
import FindDuplicateLocations from "containers/Locations/duplicates";
import MulchCalculatorLanding from "containers/MulchCalculator";
import Proposals from "containers/Proposals";
import ProposalsList from "containers/Proposals/List";
import ProposalDetail from "containers/Proposals/Detail";
import PublicProposal from "containers/Proposals/Public";
import ProposalFeedback from "containers/Proposals/Feedback";
import LeadsList from "containers/Leads/List";
import NewLead from "containers/Leads/New";
import LeadRecord from "containers/Leads/Record";
import PublicQuote from "containers/Leads/PublicQuote";
import ImportLeads from "containers/Leads/Import";
import LeadDuplicates from "containers/Leads/Duplicates";
import MaintenanceOpportunities from "containers/MaintenanceOpportunities/Queue";
import MulchCalculatorEditor from "containers/MulchCalculator/MapEditor";

import PortalLocations from "containers/Portal/Locations";

import WorkOrderReviewQueue from "containers/Maintenance/WorkOrderReviewQueue";
import WorkOrderDetail from "containers/Maintenance/WorkOrderDetail";
import MaintenanceIndex from "containers/Maintenance/MaintenanceIndex";
import Reports from "containers/Reports";
import Calendar from "containers/Calendar";
import QueueDrillDown from "containers/Dashboard/QueueDrillDown";
import PlanDetail from "containers/Maintenance/PlanDetail";

import * as Sentry from "@sentry/react";


Sentry.init({
  dsn: "https://a06f495011f6bf68a13b2d865690e1e7@o4509294417412096.ingest.us.sentry.io/4509294419247104",
  environment: process.env.REACT_APP_ENVIRONMENT || "development",
  sendDefaultPii: true,
  enabled: !!process.env.REACT_APP_SENTRY_DSN,
});

axios.defaults.baseURL = process.env.REACT_APP_BACKEND;

// We do this to all outgoing requests automatically get bearer token if we're logged in.
// This is fine because the vast majority of requests are for protected area (its a dashboard afterall)
axios.interceptors.request.use(
  function (config) {
    const user = localStorage.getItem("user");
    if (user && !config?.url?.includes("/auth/refresh")) {
      const userData = JSON.parse(user);
      config.headers["Authorization"] = `Bearer ${userData.tokens.accessToken}`;
      return config;
    } else {
      return config;
    }
  },
  function (error) {
    return Promise.reject(error);
  }
);

const PrivateRoutes = () => {
  const { setError } = useError();
  const { authenticate } = useAuthService();
  const authenticatedRef = useRef(false);
  const [initialized, setInitialized] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location) {
      setError(false);
    }
  }, [location?.pathname]);

  const checkAuthentication = async () => {
    authenticatedRef.current = await authenticate();
    setInitialized(true);
  };

  useEffect(() => {
    if (!initialized) {
      checkAuthentication();
    }
  }, [initialized]);

  if (!initialized) return null;
  return authenticatedRef.current ? <Outlet /> : <Navigate to={`/login?redirect=${encodeURIComponent(location?.pathname)}`} />;
};

function App() {
  const { error, setError } = useError();
  const isMounted = RenderCompleted();
  const hasNewVersion = useVersionCheck();

  useEffect(() => {
    if (hasNewVersion) {
      if (window.confirm("A new version is available. Refresh to update?")) {
        window.location.reload();
      }
    }
  }, [hasNewVersion]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      function (response) {
        return response;
      },
      async function (err) {
        const originalRequest = err.config;
        const user = localStorage.getItem("user");

        // A stale access token 401s every request, not just GETs. Only GETs
        // used to be retried after a refresh, so saving a proposal after a
        // long builder session failed with "Unauthorized" while the rest of
        // the app loaded fine (2026-09-28). A 401 means the server never ran
        // the request, so replaying a POST/PUT after refreshing is safe.
        if (originalRequest.excludeInterceptor || originalRequest._retry) {
          return Promise.reject(err); // Exclude this request from interceptor
        }
        if (originalRequest.method !== "get" && err.response?.status !== 401) {
          return Promise.reject(err);
        }

        /*
        if (err.response.status >= 500 && err.response.status <= 599 &&!originalRequest._retry) {
          setError("500");
        } else 
        */
        if (err.response.status === 404 && !originalRequest._retry) {
          setError("404");
        } else if (err.response.status === 403 && !originalRequest._retry) {
          setError("403");
        } else if (err.response.status === 401) {
          if (!err.response.request.responseURL.includes("/login")) {
            originalRequest._retry = true;
            //if user is in local storage use refresh token to update token
            if (user) {
              const userData = JSON.parse(user);
              localStorage.removeItem("user");
              const res = await refreshToken({ data: { id: userData.id, refreshToken: userData.tokens.refreshToken } });
              if (!res?.tokens?.accessToken) {
                window.location.assign("/login");
                return Promise.reject(err);
              }
              localStorage.setItem("user", JSON.stringify({ user_id: res?.user.id, tokens: { ...res?.tokens } }));
              originalRequest.headers = { ...(originalRequest.headers ?? {}), Authorization: `Bearer ${res.tokens.accessToken}` };
              return axios(originalRequest);
            } else {
              window.location.assign("/login");
              localStorage.removeItem("user");
              return Promise.reject(err);
            }
          } else {
            return Promise.reject(err);
          }
        } else {
          return Promise.reject(err);
        }
      }
    );

    // Clean up the interceptor when the component unmounts
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <TableRefreshProvider>
      <UserProvider>
        <Theme>
          <BrowserRouter>
            <Routes>
              <Route element={<LoadingLayout />}>
                <Route path="/" />
              </Route>

              <Route element={<PrivateRoutes />}>
                <Route element={<BaseLayout />}>
                  {error === "403" && <Route path="/*" element={<ForbiddenPage />} />}
                  {error === "404" && <Route path="/*" element={<NotFoundPage />} />}
                  {!error && (
                    <React.Fragment>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/dashboard/queue/:rowId" element={<QueueDrillDown />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/calendar" element={<Calendar />} />
                      <Route path="/users" element={<Users />} />

                      <Route path="/roles" element={<Roles />} />
                      <Route path="/roles/create" element={<RoleCreate />} />
                      <Route path="/roles/:id" element={<RoleEdit />} />
                      <Route path="/company" element={<Company />} />
                      <Route path="/client-company" element={<ClientCompany />} />
                      <Route path="/settings" element={<Settings />} />

                      <Route path="/clients" element={<Clients />} />
                      <Route path="/clients/create" element={<ClientCreate />} />
                      <Route path="/clients/:id" element={<Client />} />
                      <Route path="/clients/:id/edit" element={<ClientEdit />} />

                      <Route path="/providers" element={<Providers />} />
                      <Route path="/providers/create" element={<ProviderCreate />} />
                      <Route path="/providers/:id" element={<Provider />} />
                      <Route path="/providers/:id/edit" element={<ProviderEdit />} />
                      <Route path="/providers/:providerId/depots/:id" element={<Depot />} />

                      <Route path="/products" element={<Products />} />
                      <Route path="/products/create" element={<ProductCreate />} />
                      <Route path="/products/:id" element={<Product />} />

                      <Route path="/services" element={<Services />} />
                      <Route path="/services/create" element={<ServiceCreate />} />
                      <Route path="/services/:id" element={<Service />} />

                      <Route path="/service-areas" element={<ServiceArea />} />

                      <Route path="/quote-requests" element={<QuoteRequests />} />
                      <Route path="/quote-requests/duplicates" element={<RfqDuplicates />} />
                      <Route path="/quote-requests/:id" element={<QuoteRequest />} />
                      <Route path="/quote-requests/:id/edit" element={<QuoteRequestEdit />} />

                      <Route path="/quotes" element={<Quotes />} />
                      <Route path="/quotes/:id" element={<Quote />} />
                      <Route path="/quotes/:id/edit" element={<QuoteEdit />} />

                      <Route path="/invoices" element={<Invoices />} />
                      <Route path="/invoices/:id" element={<Invoice />} />
                      <Route path="/invoices/:id/edit" element={<InvoiceEdit />} />

                      <Route path="/purchase-orders" element={<PurchaseOrders />} />
                      <Route path="/purchase-orders/:id" element={<PurchaseOrder />} />
                      <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderEdit />} />
                      <Route path="/purchase-orders/:id/replace" element={<PurchaseOrderReplace />} />

                      <Route path="/provider-invoice-review" element={<ProviderInvoiceReview />} />

                      <Route path="/maintenance" element={<MaintenanceIndex />} />
                      <Route path="/maintenance/plans/:id" element={<PlanDetail />} />
                      <Route path="/maintenance/work-orders" element={<WorkOrderReviewQueue />} />
                      <Route path="/maintenance/work-orders/:id" element={<WorkOrderDetail />} />

                      <Route path="/projects" element={<Projects />} />
                      <Route path="/projects/:id" element={<Project />} />
                      <Route path="/projects/:id/edit" element={<ProjectEdit />} />
                      <Route path="/projects/:id/add-quote" element={<ProjectQuoteCreate />} />
                      <Route path="/projects/:id/add-invoice/:quoteId" element={<ProjectInvoiceCreate />} />

                      <Route path="/clients/users" element={<ClientUsers />} />
                      <Route path="/clients/groups" element={<Groups />} />
                      <Route path="/clients/regions" element={<Regions />} />

                      <Route path="/ai-chat" element={<AIChat />} />

                      <Route path="/companies" element={<Companies />} />
                      <Route path="/supers" element={<Supers />} />
                      {/* Expiration settings now live in Company Settings > Expiration */}
                      <Route path="/expiration-settings" element={<Navigate to="/company?tab=expiration" replace />} />

                      <Route path="/locations" element={<Locations />} />
                      <Route path="/locations/duplicates" element={<FindDuplicateLocations />} />
                      <Route path="/locations/:id" element={<LocationDetail />} />
                      <Route path="/mulch-calculator" element={<MulchCalculatorLanding />} />
                      {/* Proposal generator. Internal only — company users,
                          never the client portal. Build spec §11. */}
                      <Route path="/proposals" element={<ProposalsList />} />
                      <Route path="/proposals/new" element={<Proposals />} />
                      <Route path="/proposals/feedback" element={<ProposalFeedback />} />
                      <Route path="/proposals/:id/edit" element={<Proposals />} />
                      <Route path="/proposals/:id" element={<ProposalDetail />} />
                      {/* Leads module. Company users only; the API 403s until
                          the LEADS_MODULE_ENABLED company setting is on.
                          docs/leads/LEADS_MODULE_SCOPE.md s.16. */}
                      <Route path="/leads" element={<LeadsList />} />
                      <Route path="/leads/new" element={<NewLead />} />
                      <Route path="/leads/import" element={<ImportLeads />} />
                      <Route path="/leads/duplicates" element={<LeadDuplicates />} />
                      {/* Maintenance opportunities: completed sites coming due.
                          API 403s unless MAINTENANCE_OPPORTUNITIES_ENABLED.
                          Must sit above /leads/:id. */}
                      <Route path="/leads/opportunities" element={<MaintenanceOpportunities />} />
                      <Route path="/leads/maintenance" element={<Navigate to="/leads/opportunities" replace />} />
                      {/* Old standalone Lead settings page -> Company Settings > Leads */}
                      <Route path="/lead-settings" element={<Navigate to="/company?tab=leads" replace />} />
                      <Route path="/leads/:id" element={<LeadRecord />} />
                      <Route path="/mulch-calculator/:locationId/maps/:mapGroupId/edit" element={<MulchCalculatorEditor />} />
                      <Route path="/mulch-calculator/:locationId/edit" element={<MulchCalculatorEditor />} />
                      <Route path="/mulch-calculator/:locationId" element={<MulchCalculatorEditor />} />

                      {/* N1 Release 1 - client portal Locations page.
                          Registered flat like every other route; the
                          backend endpoint enforces role + capability +
                          ownership, and the nav link is gated on the
                          client's portalLocationsEnabled flag. */}
                      <Route path="/portal/locations" element={<PortalLocations />} />

                      <Route path="/*" element={<NotFoundPage />} />
                    </React.Fragment>
                  )}
                </Route>
              </Route>

              <Route element={<LoginLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forbidden" element={<ForbiddenPage />} />
                <Route path="/*" element={<NotFoundPage />} />
              </Route>
              <Route element={<DispatchLayout />}>
                <Route path="/dispatches/:id" element={<Dispatch />} />
              </Route>

              {/* Prospect-facing proposal page - no auth, no app chrome */}
              <Route path="/p/:token" element={<PublicProposal />} />
              {/* Lead-facing quote page - no auth, no app chrome (s.16.3) */}
              <Route path="/q/:token" element={<PublicQuote />} />
            </Routes>
          </BrowserRouter>
        </Theme>
      </UserProvider>
    </TableRefreshProvider>
  );
}

export default withToastProvider(App);
